"use client";

import { useRef, useState, useEffect } from "react";
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import { SITE, REVIEWS } from "@/lib/siteData";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating
              ? "fill-[#E8BD4E] text-[#7A5500]"
              : "fill-slate-300 text-slate-700"
          }`}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  // Lazy-reveal the off-screen carousel cards only once the section nears the
  // viewport. Rendering all wide (w-[85vw]) cards up front builds a multi-
  // viewport-wide horizontal-scroll track far off-screen, which delays/suppresses
  // the page's Largest Contentful Paint (Chromium NO_LCP / pinned-late LCP).
  // Keeping the extra cards out of layout until needed lets the hero register as
  // LCP. All cards stay in the DOM for SEO; extras reveal off-screen (no CLS).
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return;
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);

  function updateScrollState() {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);

    // Calculate active dot based on scroll position
    const cardWidth = el.scrollWidth / REVIEWS.length;
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(index, REVIEWS.length - 1));
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    updateScrollState();
    // Recompute again on next frame and after a short delay — covers
    // post-hydration layout shifts and lazy-revealed cards.
    const raf = requestAnimationFrame(updateScrollState);
    const timer = window.setTimeout(updateScrollState, 350);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [revealed]);

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current;
    if (!el) return;
    // Use the first review card, skipping any non-card nodes (e.g. <style>).
    const cardWidth =
      el.querySelector<HTMLElement>(":scope > div[data-review-card]")
        ?.offsetWidth ??
      el.clientWidth * 0.85;
    el.scrollBy({
      left: direction === "left" ? -cardWidth - 16 : cardWidth + 16,
      behavior: "smooth",
    });
  }

  return (
    <section ref={sectionRef} className="bg-[#FFFFFF] py-16 lg:py-24 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight">
            What Our Customers Say
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Real reviews from real Maryland homeowners.
          </p>
        </div>

        {/* No hard-coded rating / review counts — they go stale. Link to the live Google reviews. */}
        <div className="mb-10 text-center">
          <a
            href={SITE.googleMapsReviews}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#7A5500] underline"
          >
            <GoogleIcon className="h-4 w-4" />
            Read our Google reviews
          </a>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Desktop Arrows */}
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className={`hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-[#EEE9DD] text-[#0A0A0A] hover:bg-[#DDD5C2] transition-colors ${
              !canScrollLeft ? "opacity-30 cursor-not-allowed" : ""
            }`}
            aria-label="Previous reviews"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className={`hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-[#EEE9DD] text-[#0A0A0A] hover:bg-[#DDD5C2] transition-colors ${
              !canScrollRight ? "opacity-30 cursor-not-allowed" : ""
            }`}
            aria-label="Next reviews"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Scrollable Cards */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <style>{`
              [data-reviews-scroll]::-webkit-scrollbar { display: none; }
            `}</style>
            {REVIEWS.map((review, i) => (
              <div
                key={i}
                data-review-card
                className="snap-start shrink-0 w-[85vw] sm:w-[70vw] md:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] bg-[#F5F1E8] border border-stone-200 rounded-2xl p-6 flex flex-col"
              >
                {/* Top: Avatar + Info */}
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${review.gradientFrom}, ${review.gradientTo})`,
                    }}
                  >
                    {review.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#0A0A0A] text-sm truncate">
                        {review.name}
                      </span>
                      {/* Only the Google reviews get the Google mark; the older site testimonials are not from Google. */}
                      {review.city === "Verified Google Review" && (
                        <GoogleIcon className="h-4 w-4 shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {review.city} &middot; {review.date}
                    </div>
                  </div>
                </div>

                {/* Stars */}
                <Stars rating={review.rating} />

                {/* Quote */}
                <div className="mt-3 flex-1 relative">
                  <Quote className="h-5 w-5 text-[#7A5500]/30 absolute -top-1 -left-1" />
                  <p className="text-sm text-slate-600 leading-relaxed pl-5">
                    {review.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Pagination Dots */}
          <div className="flex md:hidden items-center justify-center gap-2 mt-4">
            {REVIEWS.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === activeIndex ? "bg-[#E8BD4E]" : "bg-slate-600"
                }`}
              />
            ))}
          </div>
        </div>

        {/* CTA — Google Maps Reviews + Leave Review */}
        <div className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={SITE.googleMapsReviews}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 cta-gold text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-all min-h-[48px] shadow-lg shadow-[#E8BD4E]/30 hover:shadow-xl hover:scale-[1.02]"
          >
            <GoogleIcon className="h-5 w-5" />
            Read All Reviews on Google
            <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={SITE.googleMaps}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-[#B98002] hover:border-[#7A5500] text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-colors min-h-[48px]"
          >
            View Us on Google Maps
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
